import { Link, useNavigate } from "react-router-dom"
import { observer, useLocalObservable } from "mobx-react"
import { Button, List, Select, Space, Table, Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"
import { choose } from "choose-matching"

import { LoadingBlock } from "@src/app/components/LoadingBlock"
import { NoContent } from "@src/app/components/NoContent/Index"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { PaymentsEntity } from "@src/app/paymentsStore/PaymentsEntity"
import { PaymentRefundEntity } from "@src/pages/refund/classes/PaymentRefundEntity"
import { GRefundPage } from "@src/pages/refund/classes/RefundPage"
import {
  DepositPayment,
  TransactionsDepositsCollection,
} from "@src/pages/transactionsDeposits/classes/TransactionsDepositsCollection"
import {
  DepositPaymentResponseStatusEnum,
  PaymentAmlLevelEnum,
} from "@src/shared/api/generated"
import { DateTimeTableCell } from "@src/shared/componemts/DateTimeTableCell"
import { TableExpandable } from "@src/shared/componemts/TableExpandable"
import { TableFilters } from "@src/shared/componemts/TableFilter"
import { WithCopy } from "@src/shared/componemts/WithCopy"
import { dateTimeFormat } from "@src/shared/date"
import { getExplorerUrl } from "@src/shared/getExplorerUrl"
import { paddingLimit } from "@src/shared/strings"
import css from "./index.module.css"

const columns: ColumnsType<DepositPayment> = [
  {
    dataIndex: "id",
    render: (_, { id }) => (
      <WithCopy
        content={id}
        view={<Typography.Text>{paddingLimit(id, 0, 3)}</Typography.Text>}
      />
    ),
    title: "ID",
  },
  {
    render: (_, { deposit_id }) => (
      <WithCopy
        content={deposit_id}
        view={
          <Typography.Text>{paddingLimit(deposit_id, 0, 3)}</Typography.Text>
        }
      />
    ),
    title: "Deposit ID",
  },
  {
    render: (_, { address }) => {
      return paddingLimit(address, 0, 3)
    },
    title: "Address",
  },
  {
    render: (_, deposit) => {
      const operationWithUrl = getExplorerUrl(
        [deposit],
        GlobalCurrenciesCollection().items,
      )[0]

      return (
        <WithCopy
          content={deposit.tx_id}
          view={
            <Typography.Link
              href={operationWithUrl.tx_explorer_url + deposit.tx_id}
              target="_blank"
            >
              {paddingLimit(deposit.tx_id, 0, 3)}
            </Typography.Link>
          }
        />
      )
    },
    title: "Tx ID",
    width: 150,
  },
  {
    dataIndex: "amount",
    render: (_, { amount }) => {
      return (
        <Typography.Text style={{ fontWeight: 700 }}>{amount}</Typography.Text>
      )
    },
    title: "Amount",
  },
  {
    dataIndex: "network",
    title: "Network",
  },
  {
    dataIndex: "symbol",
    render: (_, { network, symbol }) => {
      const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
        network,
        symbol,
      })

      return (
        <Typography.Text>{currency?.symbol_alias ?? symbol}</Typography.Text>
      )
    },
    title: "Symbol",
  },
  {
    render: (_, { status }) => {
      const cMap = {
        [DepositPaymentResponseStatusEnum.Confirmed]: "green",
        [DepositPaymentResponseStatusEnum.Failed]: "error",
        [DepositPaymentResponseStatusEnum.Pending]: "geekblue",
      }

      return <Tag color={cMap[status] ?? "geekblue"}>{status}</Tag>
    },
    title: "Status",
  },
  {
    dataIndex: "aml_level",
    render: (_, { aml_level }) => {
      const cMap = {
        [PaymentAmlLevelEnum.High]: "error",
        [PaymentAmlLevelEnum.Low]: "green",
        [PaymentAmlLevelEnum.Medium]: "warning",
      }

      return <Tag color={cMap[aml_level]}>{aml_level}</Tag>
    },
    title: "AML Level",
  },
  {
    dataIndex: "detected_at",
    render: (_, { detected_at }) => <DateTimeTableCell value={detected_at} />,
    title: "Detected at",
  },
]

export const TransactionsDepositsPage = observer(() => {
  const transactions = useLocalObservable(
    () => new TransactionsDepositsCollection(),
  )

  return (
    <div className={css.container}>
      <TableFilters {...transactions.filter.props} />
      {choose(
        [transactions.isLoading && transactions.isEmpty, <LoadingBlock />],
        [
          transactions.isEmpty,
          <NoContent
            paragraph={
              transactions.filter.state.status
                ? `You have not deposits with the status ${transactions.filter.state.status}`
                : "Wait for your first deposit to start receiving reports."
            }
            title="No Deposits Yet"
          />,
        ],
        [
          !transactions.isEmpty,
          <Table
            columns={columns}
            dataSource={transactions.items}
            expandable={{
              expandedRowRender: (record) => (
                <ListExpandable
                  deposit={record}
                  isLoading={transactions.isLoading}
                />
              ),
            }}
            loading={transactions.isLoading}
            pagination={transactions.pagination}
            rowKey="id"
            scroll={{ x: true }}
          />,
        ],
      )}
    </div>
  )
})
function ListExpandable({
  deposit,
  isLoading,
}: {
  deposit: DepositPayment
  isLoading: boolean
}) {
  const {
    address,
    aml_level,
    amount,
    deposit_id,
    detected_at,
    exchange,
    id,
    network,
    status,
    symbol,
    tx_explorer_url,
    tx_id,
  } = deposit

  const operationWithUrl = getExplorerUrl(
    [deposit],
    GlobalCurrenciesCollection().items,
  )[0]

  const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
    network,
    symbol,
  })

  const exchangeCurrency = exchange
    ? GlobalCurrenciesCollection().getBySymbolNetwork({
        network: exchange.network!,
        symbol: exchange.symbol!,
      })
    : undefined

  const nav = useNavigate()

  const makeRefund = () => {
    GRefundPage().setEntity(
      new PaymentRefundEntity(new PaymentsEntity(deposit)),
    )

    nav(paths.refund)
  }

  const cMapStatus = {
    [DepositPaymentResponseStatusEnum.Confirmed]: "green",
    [DepositPaymentResponseStatusEnum.Failed]: "error",
    [DepositPaymentResponseStatusEnum.Pending]: "geekblue",
  }

  const cMap = {
    [PaymentAmlLevelEnum.High]: "error",
    [PaymentAmlLevelEnum.Low]: "green",
    [PaymentAmlLevelEnum.Medium]: "warning",
  }

  return (
    <TableExpandable
      actions={
        !deposit.refund_id ? (
          <Button
            id="GENERATED_ID_TRANSACTIONSDEPOSITS_BUTTON_REFUND_1"
            onClick={makeRefund}
            size="large"
            type="primary"
          >
            Refund
          </Button>
        ) : undefined
      }
      isLoading={isLoading}
      list={[
        ["ID", <WithCopy content={id} />],
        ["Deposit ID", <WithCopy content={deposit_id} />],
        ["Address", <WithCopy content={address} />],
        [
          "Tx ID",
          <WithCopy
            content={tx_id}
            view={
              <Typography.Link
                href={operationWithUrl.tx_explorer_url + deposit.tx_id}
                target="_blank"
              >
                {tx_id}
              </Typography.Link>
            }
          />,
        ],
        ...([
          deposit.refund_id
            ? [
                "Refund ID",
                <WithCopy
                  content={deposit.refund_id}
                  view={<Typography.Text>{deposit.refund_id}</Typography.Text>}
                />,
              ]
            : [],
        ] as any),
        ["Network", `${network}`],
        ["Symbol", `${currency?.symbol_alias ?? symbol}`],
        [
          "Amount",
          <Typography style={{ fontWeight: 600 }}>{amount}</Typography>,
        ],
        ["Status", <Tag color={cMapStatus[status]}>{status}</Tag>],
        ["AML Level", <Tag color={cMap[aml_level]}>{aml_level}</Tag>],
        ["Detected at", dateTimeFormat(detected_at as string)],
        [
          "Auto exchange",
          exchange
            ? `Exchange to ${exchange.amount} ${exchangeCurrency?.symbol_alias ?? exchange.symbol} (${exchange.network}) with status ${exchange.status}`
            : undefined,
        ],
      ]}
    />
  )
}
